import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    // Steps
    "step1",
    "step2",
    "step3",
    "step4",
    // Progress indicators
    "progressStep1",
    "progressStep2",
    "progressStep3",
    "progressStep4",
    "progressLine1",
    "progressLine2",
    "progressLine3",
    // Form elements
    "form",
    "classroomSelect",
    "classroomError",
    "fileInput",
    "sheetNameInput",
    // File upload
    "dropZone",
    "uploadIcon",
    "uploadText",
    "fileInfo",
    "fileName",
    "rowCount",
    "loadingIndicator",
    "errorMessage",
    "errorText",
    // Mapping
    "mappingContainer",
    "mappingSectionTemplate",
    "fieldMappingTemplate",
    // Summary
    "summaryClassroom",
    "summaryFile",
    "summaryStudentCount",
    "summaryParentsCreated",
    "summaryParentsLinked",
    // Aperçu avant import
    "dryRun",
    "dryRunLoading",
    "dryRunResult",
    "dryRunCounters",
    "dryRunRows",
    // Buttons
    "nextButton",
    "prevButton",
    "submitButton",
    "cancelButton",
  ];

  static values = {
    currentStep: { type: Number, default: 1 },
    previewUrl: String,
    dryRunUrl: String,
    translations: Object,
  };

  connect() {
    this.currentStepValue = 1;
    this.fileData = null;
    this.analysisResult = null;
    this.updateStepVisibility();
  }

  // Navigation
  nextStep() {
    if (!this.validateCurrentStep()) return;

    if (this.currentStepValue < 4) {
      this.currentStepValue++;
      this.updateStepVisibility();
      this.updateProgressIndicators();

      if (this.currentStepValue === 3) {
        this.renderColumnMappings();
      } else if (this.currentStepValue === 4) {
        this.updateSummary();
        this.runDryRun();
      }
    }
  }

  previousStep() {
    if (this.currentStepValue > 1) {
      this.currentStepValue--;
      this.updateStepVisibility();
      this.updateProgressIndicators();
    }
  }

  updateStepVisibility() {
    // Hide all steps
    this.step1Target.classList.add("hidden");
    this.step2Target.classList.add("hidden");
    this.step3Target.classList.add("hidden");
    this.step4Target.classList.add("hidden");

    // Show current step
    this[`step${this.currentStepValue}Target`].classList.remove("hidden");

    // Barre d'actions : « Retour » à partir de l'étape 2, « Lancer l'import »
    // à la dernière seulement, « Suivant » partout ailleurs. « Annuler » ne
    // sert que là où « Retour » manque, c'est-à-dire à l'étape 1 — les deux
    // côte à côte feraient doublon.
    //
    // Via l'ATTRIBUT hidden et non la classe : `.ds-btn { display }` bat
    // l'utilitaire `.hidden` (spécificité égale, `.hidden` déclaré avant le
    // DS), donc les boutons s'affichaient à toutes les étapes.
    const onFirstStep = this.currentStepValue === 1;
    this.prevButtonTarget.hidden = onFirstStep;
    this.cancelButtonTarget.hidden = !onFirstStep;

    const onLastStep = this.currentStepValue === 4;
    this.nextButtonTarget.hidden = onLastStep;
    this.submitButtonTarget.hidden = !onLastStep;
  }

  updateProgressIndicators() {
    for (let i = 1; i <= 4; i++) {
      const stepTarget = this[`progressStep${i}Target`];
      if (i <= this.currentStepValue) {
        stepTarget.classList.remove("bg-gray-200", "text-gray-600");
        stepTarget.classList.add("bg-cta", "text-white");
      } else {
        stepTarget.classList.remove("bg-cta", "text-white");
        stepTarget.classList.add("bg-gray-200", "text-gray-600");
      }
    }

    // Update progress lines
    for (let i = 1; i <= 3; i++) {
      const lineTarget = this[`progressLine${i}Target`];
      if (lineTarget) {
        if (i < this.currentStepValue) {
          lineTarget.style.width = "100%";
        } else {
          lineTarget.style.width = "0%";
        }
      }
    }
  }

  validateCurrentStep() {
    switch (this.currentStepValue) {
      case 1:
        if (!this.classroomSelectTarget.value) {
          this.classroomSelectTarget.focus();
          this.classroomSelectTarget.classList.add("border-red-500");
          this.classroomSelectTarget.classList.remove("border-gray-300");
          if (this.hasClassroomErrorTarget) {
            this.classroomErrorTarget.classList.remove("hidden");
          }
          return false;
        }
        this.clearClassroomError();
        return true;

      case 2:
        if (!this.fileData) {
          this.dropZoneTarget.classList.add("border-red-500");
          return false;
        }
        return true;

      case 3:
        // Check required fields are mapped
        const requiredFields = [
          "first_name",
          "last_name",
          "birth_date",
          "gender",
        ];
        for (const field of requiredFields) {
          const select = this.element.querySelector(
            `select[name="student_import[${field}_column]"]`,
          );
          if (select && select.value === "-1") {
            select.classList.add("border-red-500");
            select.focus();
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  }

  clearClassroomError() {
    this.classroomSelectTarget.classList.remove("border-red-500");
    this.classroomSelectTarget.classList.add("border-gray-300");
    if (this.hasClassroomErrorTarget) {
      this.classroomErrorTarget.classList.add("hidden");
    }
  }

  // File handling
  handleDragOver(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.add("border-cta", "bg-cta-shared");
  }

  handleDragLeave(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.remove("border-cta", "bg-cta-shared");
  }

  handleDrop(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.remove("border-cta", "bg-cta-shared");

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (this.validateFileType(file)) {
        this.fileInputTarget.files = files;
        this.processFile(file);
      }
    }
  }

  analyzeFile(event) {
    const file = event.target.files[0];
    if (file && this.validateFileType(file)) {
      this.processFile(file);
    }
  }

  validateFileType(file) {
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (!validTypes.includes(file.type) && !file.name.endsWith(".xlsx")) {
      this.showError(
        this.translationsValue.file_error ||
          "Format de fichier invalide. Utilisez un fichier .xlsx",
      );
      return false;
    }
    return true;
  }

  async processFile(file) {
    this.fileData = file;
    this.showLoading();
    this.hideError();

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            .content,
        },
      });

      const result = await response.json();

      if (result.error) {
        this.showError(result.error);
        this.fileData = null;
        return;
      }

      this.analysisResult = result;
      this.showFileInfo(file, result);

      // Store sheet name
      if (result.sheets && result.sheets.length > 0) {
        this.sheetNameInputTarget.value = result.sheets[0].name;
      }
    } catch (error) {
      console.error("Error analyzing file:", error);
      this.showError("Erreur lors de l'analyse du fichier");
      this.fileData = null;
    }
  }

  showLoading() {
    this.dropZoneTarget.classList.add("hidden");
    this.loadingIndicatorTarget.classList.remove("hidden");
    this.fileInfoTarget.classList.add("hidden");
  }

  showFileInfo(file, result) {
    this.loadingIndicatorTarget.classList.add("hidden");
    this.dropZoneTarget.classList.add("hidden");
    this.fileInfoTarget.classList.remove("hidden");

    this.fileNameTarget.textContent = file.name;

    let totalRows = 0;
    if (result.sheets) {
      totalRows = result.sheets.reduce(
        (sum, sheet) => sum + (sheet.row_count || 0),
        0,
      );
    }

    const rowsText =
      this.translationsValue.rows_detected || "%{count} lignes détectées";
    this.rowCountTarget.textContent = rowsText.replace("%{count}", totalRows);
  }

  showError(message) {
    this.loadingIndicatorTarget.classList.add("hidden");
    this.dropZoneTarget.classList.remove("hidden");
    this.fileInfoTarget.classList.add("hidden");
    this.errorMessageTarget.classList.remove("hidden");
    this.errorTextTarget.textContent = message;
  }

  hideError() {
    this.errorMessageTarget.classList.add("hidden");
  }

  clearFile() {
    this.fileData = null;
    this.analysisResult = null;
    this.fileInputTarget.value = "";
    this.sheetNameInputTarget.value = "";
    this.dropZoneTarget.classList.remove("hidden");
    this.fileInfoTarget.classList.add("hidden");
  }

  // Column Mapping
  renderColumnMappings() {
    if (!this.analysisResult || !this.analysisResult.sheets) return;

    const container = this.mappingContainerTarget;
    container.innerHTML = "";

    const sheet = this.analysisResult.sheets[0];
    if (!sheet) return;

    const columns = sheet.columns || [];
    const suggestedMapping = sheet.suggested_mapping || {};

    // Define field sections
    const sections = [
      {
        title: "INFORMATIONS ÉLÈVE",
        icon: "bg-blue-100 text-blue-600",
        fields: [
          { key: "first_name", label: "Prénom", required: true },
          { key: "last_name", label: "Nom", required: true },
          { key: "birth_date", label: "Date de naissance", required: true },
          {
            key: "place_of_birth",
            label: "Lieu de naissance",
            required: false,
          },
          { key: "gender", label: "Sexe", required: true },
          { key: "email", label: "Email", required: false },
          { key: "registration_number", label: "Matricule", required: false },
          { key: "has_access", label: "Accès application", required: false },
        ],
      },
      {
        title: "INFORMATIONS MÈRE",
        icon: "bg-pink-100 text-pink-600",
        fields: [
          { key: "mother_first_name", label: "Prénom mère", required: false },
          { key: "mother_last_name", label: "Nom mère", required: false },
          { key: "mother_phone", label: "Téléphone mère", required: false },
          { key: "mother_email", label: "Email mère", required: false },
        ],
      },
      {
        title: "INFORMATIONS PÈRE",
        icon: "bg-indigo-100 text-indigo-600",
        fields: [
          { key: "father_first_name", label: "Prénom père", required: false },
          { key: "father_last_name", label: "Nom père", required: false },
          { key: "father_phone", label: "Téléphone père", required: false },
          { key: "father_email", label: "Email père", required: false },
        ],
      },
    ];

    sections.forEach((section) => {
      const sectionDiv = document.createElement("div");
      sectionDiv.className = "mb-8";

      // Section header
      const header = document.createElement("h3");
      header.className =
        "text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 flex items-center";
      header.innerHTML = `
        <span class="w-8 h-8 rounded-full ${section.icon} flex items-center justify-center mr-3">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
          </svg>
        </span>
        ${section.title}
      `;
      sectionDiv.appendChild(header);

      // Fields grid
      const grid = document.createElement("div");
      grid.className = "grid grid-cols-1 md:grid-cols-2 gap-4";

      section.fields.forEach((field) => {
        const fieldDiv = document.createElement("div");

        const label = document.createElement("label");
        label.className = "block text-sm font-medium text-gray-700 mb-1";
        label.innerHTML = `${field.label} ${field.required ? '<span class="text-red-500">*</span>' : '<span class="text-gray-400 text-xs">(optionnel)</span>'}`;

        const selectWrapper = document.createElement("div");
        selectWrapper.className = "relative";

        const select = document.createElement("select");
        select.name = `student_import[${field.key}_column]`;
        select.className =
          "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cta focus:border-cta text-sm";

        // Add default option
        const defaultOption = document.createElement("option");
        defaultOption.value = "-1";
        defaultOption.textContent =
          this.translationsValue.select_column || "-- Sélectionner --";
        select.appendChild(defaultOption);

        // Add column options
        columns.forEach((col, index) => {
          const option = document.createElement("option");
          option.value = index;
          option.textContent = `Colonne ${index + 1}: ${col}`;
          select.appendChild(option);
        });

        // Auto-select if suggested
        if (suggestedMapping[field.key] !== undefined) {
          select.value = suggestedMapping[field.key];

          const badge = document.createElement("span");
          badge.className =
            "absolute right-8 top-1/2 -translate-y-1/2 text-xs bg-cta-shared text-cta-darker px-2 py-0.5 rounded";
          badge.textContent =
            this.translationsValue.auto_detected || "Auto-détecté";
          selectWrapper.appendChild(badge);
        }

        selectWrapper.appendChild(select);
        fieldDiv.appendChild(label);
        fieldDiv.appendChild(selectWrapper);
        grid.appendChild(fieldDiv);
      });

      sectionDiv.appendChild(grid);
      container.appendChild(sectionDiv);
    });
  }

  // Summary
  updateSummary() {
    // Classroom
    const classroomSelect = this.classroomSelectTarget;
    const selectedOption =
      classroomSelect.options[classroomSelect.selectedIndex];
    this.summaryClassroomTarget.textContent = selectedOption
      ? selectedOption.text
      : "-";

    // File
    this.summaryFileTarget.textContent = this.fileData
      ? this.fileData.name
      : "-";

    // Student count
    let studentCount = 0;
    if (
      this.analysisResult &&
      this.analysisResult.sheets &&
      this.analysisResult.sheets[0]
    ) {
      studentCount = this.analysisResult.sheets[0].row_count || 0;
    }
    this.summaryStudentCountTarget.textContent = studentCount;

    // Les responsables ne sont plus estimés : la simulation renseigne les
    // compteurs réels dès qu'elle a répondu.
    this.summaryParentsCreatedTarget.textContent = "-";
    this.summaryParentsLinkedTarget.textContent = "-";
  }

  // Aperçu avant import : on demande au serveur ce qui se passerait, sans
  // rien écrire. L'utilisateur voit les doublons et les erreurs avant de
  // lancer le traitement, pas après.
  async runDryRun() {
    if (!this.hasDryRunUrlValue || !this.fileData) return;

    this.dryRunLoadingTarget.classList.remove("hidden");
    this.dryRunResultTarget.classList.add("hidden");

    const formData = new FormData();
    formData.append("file", this.fileData);
    formData.append("sheet_name", this.sheetNameInputTarget.value || "");
    this.element
      .querySelectorAll('select[name^="student_import["]')
      .forEach((select) => {
        const field = select.name.replace("student_import[", "").replace("]", "");
        formData.append(field, select.value);
      });

    try {
      const response = await fetch(this.dryRunUrlValue, {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": this.csrfToken() },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      this.renderDryRun(data);
    } catch (error) {
      this.dryRunLoadingTarget.textContent = error.message;
    }
  }

  // Les libellés viennent des cellules du fichier : un nom contenant du HTML
  // s'exécuterait dans le navigateur de la personne qui prépare l'import.
  escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    })[c]);
  }

  renderDryRun(data) {
    const t = this.translationsValue;
    const tones = {
      create: "success",
      duplicate: "warning",
      error: "danger",
    };

    this.dryRunCountersTarget.innerHTML = ["create", "duplicate", "error"]
      .map(
        (key) =>
          `<span class="ds-chip ds-chip--${tones[key]}">${
            data.summary[key]
          } ${t[`dry_run_${key}`] || key}</span>`,
      )
      .join("");

    this.dryRunRowsTarget.innerHTML = data.rows
      .map((row) => {
        const parents = (row.parents || [])
          .map(
            (p) =>
              `${this.escapeHtml(p.name)} <span style="color:var(--ds-text-4);">(${
                p.existing ? t.dry_run_parent_linked : t.dry_run_parent_created
              })</span>`,
          )
          .join("<br>");
        const detail =
          row.status === "error"
            ? this.escapeHtml((row.messages || []).join(" — "))
            : row.status === "duplicate"
              ? t.dry_run_duplicate_detail
              : t.dry_run_create_detail;

        return `<tr>
          <td class="ds-recentrow__detail">${row.row}</td>
          <td>${this.escapeHtml(row.label) || "-"}</td>
          <td><span class="ds-chip ds-chip--${tones[row.status]}">${
            t[`dry_run_${row.status}`] || row.status
          }</span><div style="font:400 12px var(--g4s-font-sans); color:var(--ds-text-3);">${detail}</div></td>
          <td class="ds-recentrow__detail">${parents || "-"}</td>
        </tr>`;
      })
      .join("");

    this.summaryStudentCountTarget.textContent = data.summary.create;
    this.summaryParentsCreatedTarget.textContent = data.summary.parents_created;
    this.summaryParentsLinkedTarget.textContent = data.summary.parents_linked;

    this.dryRunLoadingTarget.classList.add("hidden");
    this.dryRunResultTarget.classList.remove("hidden");
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : "";
  }

  isMapped(fieldKey) {
    const select = this.element.querySelector(
      `select[name="student_import[${fieldKey}_column]"]`,
    );
    return select && select.value !== "-1";
  }
}
